export const LISTING_FINANCIAL_READ_BATCH_SIZE = 50;
export const LISTING_FINANCIAL_REFRESH_BATCH_SIZE = 25;

export const chunkListingIds = (listingIds, batchSize) => {
  const batches = [];
  for (let index = 0; index < listingIds.length; index += batchSize) {
    batches.push(listingIds.slice(index, index + batchSize));
  }
  return batches;
};

const isAbortError = (error) => error?.code === 'ERR_CANCELED';

const isRetryBackoffActive = (row) => {
  const retryAfter = Date.parse(String(row?.retryAfter || ''));
  return Number.isFinite(retryAfter) && retryAfter > Date.now();
};

const retryableFailureRows = (listingIds, getCurrentRow) => listingIds.map((listingId) => {
  const currentRow = getCurrentRow?.(listingId);
  const failureState = {
    errorCode: 'ML_ESTIMATE_FAILED',
    message: 'No pudimos obtener la estimación de Mercado Libre. Probá de nuevo en unos minutos.',
    retryAfter: currentRow?.retryAfter || null,
  };

  if (currentRow?.hasSnapshot) {
    return {
      ...currentRow,
      ...failureState,
      freshness: 'stale',
    };
  }

  return {
    listingId,
    hasSnapshot: false,
    freshness: 'missing',
    status: 'ERROR',
    ...failureState,
  };
});

const refreshableIdsFromRows = (rows) => rows
  .filter((row) => (row?.freshness === 'stale' || row?.freshness === 'missing') && !isRetryBackoffActive(row))
  .map((row) => row.listingId)
  .filter(Boolean);

/**
 * Runs the bounded cache-read and remote-refresh workflow for a visible listing set.
 * Failed batches remain visible and retryable, while independent later batches continue.
 */
export const loadListingFinancialBatches = async ({
  listingIds,
  getSnapshots,
  refreshSnapshots,
  isCurrent,
  onRows,
  onLoading,
  getCurrentRow,
}) => {
  const refreshIds = [];
  const rowsById = new Map();
  const getPreviousRow = (listingId) => rowsById.get(listingId) || getCurrentRow?.(listingId);
  const applyRows = (rows) => {
    rows.forEach((row) => rowsById.set(row.listingId, row));
    onRows(rows);
  };

  for (const batchIds of chunkListingIds(listingIds, LISTING_FINANCIAL_READ_BATCH_SIZE)) {
    if (!isCurrent()) return;
    try {
      const response = await getSnapshots(batchIds);
      if (!isCurrent()) return;
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      applyRows(rows);
      refreshIds.push(...refreshableIdsFromRows(rows));
    } catch (error) {
      if (!isCurrent() || isAbortError(error)) return;
      applyRows(retryableFailureRows(batchIds, getPreviousRow));
    }
  }

  for (const batchIds of chunkListingIds(refreshIds, LISTING_FINANCIAL_REFRESH_BATCH_SIZE)) {
    if (!isCurrent()) return;
    onLoading(batchIds, true);
    try {
      const response = await refreshSnapshots(batchIds);
      if (!isCurrent()) return;
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      applyRows(rows);
    } catch (error) {
      if (isCurrent() && !isAbortError(error)) {
        applyRows(retryableFailureRows(batchIds, getPreviousRow));
      }
    } finally {
      onLoading(batchIds, false);
    }
  }
};
